import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Tag, Plus, Pencil, Trash2, X, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const DURATIONS = [
  { v: "once", l: "Uma vez (só na primeira cobrança)" },
  { v: "repeating", l: "Recorrente por N meses" },
  { v: "forever", l: "Sempre (todas as renovações)" },
];

const emptyForm = {
  code: "",
  discount_percent: 30,
  max_uses: 0,
  expires_at: "",
  active: true,
  description: "",
  duration: "once",
  duration_in_months: 3,
};

const CouponFormModal = ({ open, coupon, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const editing = !!coupon;

  useEffect(() => {
    if (!open) return;
    if (coupon) {
      setForm({
        code: coupon.code,
        discount_percent: coupon.discount_percent,
        max_uses: coupon.max_uses || 0,
        expires_at: coupon.expires_at || "",
        active: coupon.active !== false,
        description: coupon.description || "",
        duration: coupon.duration || "once",
        duration_in_months: coupon.duration_in_months || 3,
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, coupon]);

  if (!open) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const code = (form.code || "").trim().toUpperCase();
    if (!editing && code.length < 3) { toast.error("Código deve ter no mín. 3 caracteres"); return; }
    if (form.discount_percent < 5 || form.discount_percent > 100) {
      toast.error("Desconto deve estar entre 5 e 100"); return;
    }
    if (form.duration === "repeating" && (!form.duration_in_months || form.duration_in_months < 1)) {
      toast.error("Cupom recorrente exige nº de meses ≥ 1"); return;
    }
    setBusy(true);
    try {
      if (editing) {
        // PATCH — envia só campos editáveis
        const payload = {
          discount_percent: form.discount_percent,
          max_uses: Number(form.max_uses) || 0,
          expires_at: form.expires_at || null,
          active: form.active,
          description: form.description || null,
          duration: form.duration,
          duration_in_months: form.duration === "repeating" ? Number(form.duration_in_months) : null,
        };
        await api.patch(`/admin/fp/coupons/${coupon.code}`, payload);
        toast.success(`Cupom ${coupon.code} atualizado.`);
      } else {
        const payload = {
          code,
          discount_percent: Number(form.discount_percent),
          max_uses: Number(form.max_uses) || 0,
          expires_at: form.expires_at || null,
          active: form.active,
          description: form.description || null,
          duration: form.duration,
          duration_in_months: form.duration === "repeating" ? Number(form.duration_in_months) : null,
        };
        await api.post("/admin/fp/coupons", payload);
        toast.success(`Cupom ${code} criado.`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar cupom");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="coupon-form-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <Tag className="w-5 h-5" />
            <div className="font-display font-bold text-lg">{editing ? "Editar cupom" : "Novo cupom FP"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-2 block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Código *</span>
              <input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())}
                disabled={editing} maxLength={30}
                data-testid="coupon-form-code"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm uppercase disabled:bg-slate-100 disabled:text-slate-500" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Desconto (%) *</span>
              <input type="number" min={5} max={100} value={form.discount_percent}
                onChange={(e) => set("discount_percent", Number(e.target.value))}
                data-testid="coupon-form-discount"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Descrição</span>
            <input value={form.description} onChange={(e) => set("description", e.target.value)}
              maxLength={200}
              data-testid="coupon-form-description"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Máx. usos (0 = ilimitado)</span>
              <input type="number" min={0} value={form.max_uses}
                onChange={(e) => set("max_uses", e.target.value)}
                data-testid="coupon-form-max-uses"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Expira em (opcional)</span>
              <input type="date" value={form.expires_at ? form.expires_at.slice(0, 10) : ""}
                onChange={(e) => set("expires_at", e.target.value ? `${e.target.value}T23:59:59Z` : "")}
                data-testid="coupon-form-expires"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Duração</span>
              <select value={form.duration} onChange={(e) => set("duration", e.target.value)}
                data-testid="coupon-form-duration"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                {DURATIONS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
              </select>
            </label>
            {form.duration === "repeating" && (
              <label className="block">
                <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Nº de meses *</span>
                <input type="number" min={1} max={60} value={form.duration_in_months}
                  onChange={(e) => set("duration_in_months", Number(e.target.value))}
                  data-testid="coupon-form-months"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
            )}
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
            <input type="checkbox" checked={form.active}
              onChange={(e) => set("active", e.target.checked)}
              data-testid="coupon-form-active"
              className="rounded border-slate-300" />
            Ativo (aceita novos usos)
          </label>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy}
            data-testid="coupon-form-save"
            className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Salvando…" : (editing ? "Salvar" : "Criar cupom")}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminCouponsPanel = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fp/coupons");
      setList(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao listar cupons");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(
      `Excluir cupom ${c.code}?\n\n` +
      `Já usado ${c.uses_count || 0}x — quem já assinou mantém o desconto.\n` +
      "Novos usos serão bloqueados definitivamente.",
    )) return;
    try {
      await api.delete(`/admin/fp/coupons/${c.code}`);
      toast.success(`Cupom ${c.code} excluído.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/fp/coupons/${c.code}`, { active: !c.active });
      toast.success(`Cupom ${c.code} ${c.active ? "desativado" : "ativado"}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alterar status");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-coupons-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-amber-600" />
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Cupons FP</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Códigos promocionais aplicados no checkout dos planos Filho Presente.
            Suporta desconto <b>uma vez</b>, <b>recorrente por N meses</b> ou <b>sempre</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="admin-coupons-refresh"
            className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Atualizar
          </button>
          <button onClick={() => setCreating(true)} data-testid="admin-coupons-new"
            className="text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-[#0B2B62] font-semibold rounded-md inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Novo cupom
          </button>
        </div>
      </div>

      {loading && list.length === 0 ? (
        <div className="p-8 text-slate-500 text-sm">Carregando cupons…</div>
      ) : list.length === 0 ? (
        <div className="p-8 border border-dashed border-slate-300 rounded-xl bg-slate-50 text-slate-500 text-sm text-center" data-testid="admin-coupons-empty">
          Nenhum cupom cadastrado. Clique em <b>Novo cupom</b> pra criar o primeiro.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Desconto</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Duração</th>
                <th className="text-left px-4 py-3">Usos</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Expira</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const durLabel = c.duration === "forever" ? "Sempre"
                  : c.duration === "repeating" ? `${c.duration_in_months || "?"}m`
                  : "Uma vez";
                const usesLabel = c.max_uses > 0
                  ? `${c.uses_count || 0} / ${c.max_uses}`
                  : `${c.uses_count || 0} · ∞`;
                return (
                  <tr key={c.code} className={`border-t ${!c.active ? "opacity-60" : ""}`} data-testid={`admin-coupon-row-${c.code}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0B2B62] font-mono">{c.code}</div>
                      {c.description && (
                        <div className="text-[11px] text-slate-500 mt-0.5 max-w-xs truncate">{c.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-bold text-emerald-700">{c.discount_percent}%</td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600 text-xs">{durLabel}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{usesLabel}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString("pt-BR") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleActive(c)}
                        data-testid={`admin-coupon-toggle-${c.code}`}
                        title={c.active ? "Clique pra desativar" : "Clique pra ativar"}
                        className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-bold ${
                          c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
                        }`}>
                        {c.active ? "Ativo" : "Inativo"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1.5 justify-end">
                        <button onClick={() => setEditing(c)}
                          data-testid={`admin-coupon-edit-${c.code}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                          <Pencil className="w-3.5 h-3.5" /> Editar
                        </button>
                        <button onClick={() => remove(c)}
                          data-testid={`admin-coupon-delete-${c.code}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" /> Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CouponFormModal
        open={creating}
        coupon={null}
        onClose={() => setCreating(false)}
        onSaved={load}
      />
      <CouponFormModal
        open={!!editing}
        coupon={editing}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
    </div>
  );
};

export default AdminCouponsPanel;
